import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { format } from 'app/shared/utils/string.util';
import { Metamodel } from 'app/model/base/model';
import { BlobSink } from 'app/model/pipeline/copysink/blob.copysink';
import { AzureBlobCopySource } from 'app/model/pipeline/copysource/azure-blob.copysource';
import {
    sasUriSetting,
    blobEndpointSetting,
    blobContainerUriSetting
} from 'app/registries/linked-service/azure-storage-shared-fields';
import { accountKeyConnectionStringSetting } from '../consts/metadata-consts-with-ec-deps';
import {
    commonFileTypeStoreGetMetadataActivityReadSettings,
    commonFileTypeStoreDeleteActivityReadSettings,
    getCommonBinaryStoreReadSettingControls,
    getCommonFileTypeStoreReadSettingControls,
    azureCloudTypeMetadata,
    authenticationReferenceMethodOptions,
    commonFileTypeStoreWriteSettingControls,
    commonBinaryStoreWriteSettingControls,
    cdcDestination
} from 'app/registries/consts/metadata-consts';
import { Constants } from 'app/shared/shared-constants';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { mergeUamiCredentialOption, mergeUserAuthCredentialOption } from '../utls/authenticationUtils';
import { GlobalVariables } from 'app/services/globals';
import {
    azureDefaultAuthRefTypes,
    azureStorageAuthTypes,
    CredentialTypeOptions,
    ModelConstants,
    servicePrincipalCredentialTypes
} from 'shell-models-lib';
import {
    avro,
    delimited,
    delta,
    excel,
    json,
    orc,
    parquet,
    iceberg,
    xml
} from 'app/registries/dataflow/dataflow-file-format-metadata';
import { storagePEWellknownErrorMessages } from 'app/shared/utils/connector-metadata-static-consts';
import { blockSizeInMbMetadata } from './linked-service-shared-fields';
import { DataSourceOptionKind, BuiltInDataSourceOptionId } from '@powerquery/mashup-editor-client';

const blockSizeInMbDesc =
    format(stringResources.BlockSizeInMB_Description, stringResources.AzureBlobStorage_DisplayText) +
    ' <a target="_blank" href="https://go.microsoft.com/fwlink/?linkid=2111570">' +
    stringResources.LearnMore +
    '</a>';

const tenantBase: ControlMetadata = {
    type: 'tenant-textbox',
    bind: 'tenant',
    validators: [{ type: 'required', errorMessage: '' }],
    isLabelVertical: true,
    label: stringResources.ModelName_Tenant,
    required: true
};

const tenantInput: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: tenantBase
    }
];

const servicePrincipalIdBase: ControlMetadata = {
    type: 'textbox',
    bind: 'servicePrincipalId',
    label: stringResources.ModelName_ServicePrincipalID,
    required: true,
    validators: [{ type: 'required', errorMessage: '' }],
    isLabelVertical: true
};

const servicePrincipalIdInput: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: servicePrincipalIdBase
    }
];

const servicePrincipalKeyPasswordBase: ControlMetadata = {
    type: 'password',
    label: stringResources.ModelName_ServicePrincipalKey,
    required: true,
    isLabelVertical: true,
    validators: [{ type: 'required', errorMessage: '' }]
};

const servicePrincipalCredTypeInput: ControlMetadata[] = [
    {
        type: 'radio-switch',
        bind: 'servicePrincipalCredentialType',
        nested: 1,
        hide: '{ isOldServicePrincipalResource }',
        label: stringResources.ModelName_ServicePrincipalCredentialType,
        isLabelVertical: true,
        options: [
            {
                label: stringResources.ModelName_ServicePrincipalKey,
                value: servicePrincipalCredentialTypes.ServicePrincipalKey
            },
            {
                label: stringResources.ModelName_ServicePrincipalCert,
                value: servicePrincipalCredentialTypes.ServicePrincipalCert
            }
        ],
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        defaultValue: servicePrincipalCredentialTypes.ServicePrincipalKey,
        cases: [
            {
                caseValue: servicePrincipalCredentialTypes.ServicePrincipalKey,
                children: [
                    {
                        type: 'dynamic-akv-switcher',
                        bind: 'servicePrincipalKey',
                        label: stringResources.ModelName_ServicePrincipalKey,
                        enableAKVParameterization: true,
                        child: {
                            type: 'parameterizable',
                            attribute: 'servicePrincipalKey',
                            child: servicePrincipalKeyPasswordBase,
                            disableAddParameter: true
                        },
                        description: format(
                            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(
                                stringResources.ModelName_LinkedServiceTemplate,
                                stringResources.AzureBlobStorage_DisplayText
                            ),
                            stringResources.ModelName_ServicePrincipalKey
                        )
                    }
                ]
            },
            {
                caseValue: servicePrincipalCredentialTypes.ServicePrincipalCert,
                children: [
                    {
                        type: 'dynamic-akv-switcher',
                        bind: 'servicePrincipalCert',
                        label: stringResources.ModelName_ServicePrincipalCert,
                        enableAKVParameterization: true,
                        validators: [{ type: 'required', errorMessage: '' }],
                        akvOnly: true,
                        isCert: true,
                        akvOptionDisplayText: stringResources.AzureKeyVault_ServicePrincipalCert_DisplayText,
                        description: format(
                            stringResources.AzureKeyVaultCertName_Tooltip_Template,
                            format(
                                stringResources.ModelName_LinkedServiceTemplate,
                                stringResources.AzureBlobStorage_DisplayText
                            ),
                            stringResources.ModelName_ServicePrincipalCert
                        )
                    }
                ]
            }
        ]
    }
];

// default auth options of A/c Key, SAMI and SP. Based on flag, the fourth option will be populated inside controls.
function getDefaultAuthenticationOptions(): Array<ISelectOption<string>> {
    return [
        {
            label: stringResources.Anonymous_DisplayText,
            value: azureStorageAuthTypes.Anonymous
        },
        {
            label: stringResources.AuthenticationType_AccountKey,
            value: azureStorageAuthTypes.AccountKeyAuthentication
        },
        {
            label: stringResources.SasUri,
            value: azureStorageAuthTypes.SasUriAuthentication
        },
        {
            label: stringResources.AuthenticationType_ServicePrincipal,
            value: azureStorageAuthTypes.ServicePrincipalAuthentication
        },
        {
            label: stringResources.AuthenticationType_ManagedServiceIdentity,
            value: azureStorageAuthTypes.MSIAuthentication
        }
    ];
}

const akvSwitcherInput: ControlMetadata[] = [
    {
        type: 'dynamic-akv-switcher',
        bind: 'servicePrincipalKey',
        hide: '{ !isOldServicePrincipalResource }',
        label: stringResources.ModelName_ServicePrincipalKey,
        enableAKVParameterization: true,
        child: {
            child: servicePrincipalKeyPasswordBase,
            type: 'parameterizable',
            attribute: 'servicePrincipalKey',
            disableAddParameter: true
        },
        description: format(
            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureBlobStorage_DisplayText),
            stringResources.ModelName_ServicePrincipalKey
        )
    }
];

const servicePrincipalInlineMethodChildren: ControlMetadata[] = [
    // service Principal
    ...tenantInput,
    ...servicePrincipalIdInput,
    ...servicePrincipalCredTypeInput,
    ...akvSwitcherInput,
    azureCloudTypeMetadata
];

// provides user with inline vs credential radio button to add authentication parameters for SAMI and SP Authentication methods.
function azureStorageAuthReferenceSetting(credentialTypeFilter: CredentialTypeOptions): ControlMetadata {
    return {
        type: 'radio-switch',
        bind: 'authReferenceMethod',
        nested: 1,
        label: stringResources.Authentication_Reference_Method_Label,
        description: stringResources.Authentication_Reference_Method_Description,
        options: authenticationReferenceMethodOptions,
        isLabelVertical: true,
        defaultValue: azureDefaultAuthRefTypes.InlineRefTypeAuthentication,
        cases: [
            {
                caseValue: azureDefaultAuthRefTypes.InlineRefTypeAuthentication,
                children:
                    credentialTypeFilter === CredentialTypeOptions.ServicePrincipal
                        ? servicePrincipalInlineMethodChildren
                        : [
                            // system Assigned Managed Identity
                            {
                                type: 'management-identity-info',
                                resourceType: stringResources.AzureBlobStorage_DisplayText,
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-blob-storage?tabs=data-factory#managed-identity'
                            }
                        ]
            },
            {
                caseValue: azureDefaultAuthRefTypes.CredentialRefTypeAuthentication,
                children: [
                    {
                        type: 'credential-select',
                        bind: 'credential',
                        filterCredentialType: credentialTypeFilter,
                        required: true,
                        supportFic: GlobalVariables.supportFic,
                        validators: [{ type: 'required', errorMessage: '' }]
                    }
                ]
            }
        ]
    };
}

export const azureBlobStorageMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.azureBlob.linkedServiceType,
        displayName: stringResources.AzureBlobStorage_DisplayText,
        linkedServiceKind: 'file',
        icon: {
            className: 'svg-i-100x100-AzureBlob'
        },
        supportedInTrident: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.BuiltIn,
            id: BuiltInDataSourceOptionId.AzureBlobs
        },
        category: ['Azure'],
        copySinkTypes: [Metamodel.modelFor(BlobSink).name],
        copySourceTypes: [Metamodel.modelFor(AzureBlobCopySource).name],
        isFirstClass: true,
        supportFirstClassParameterExperience: true,
        supportPE: true,
        peGroupIDs: ['blob'],
        peWellknowErrorMessages: storagePEWellknownErrorMessages,
        resourceTypes: ['Microsoft.ClassicStorage/storageAccounts', 'Microsoft.Storage/storageAccounts'],
        supportIncrementalBinaryCopy: true,
        supportDatasetV2: true,
        supportDataFlowSource: true,
        supportDataFlowSink: true,
        supportOPDG: GlobalVariables.enableOPDG,
        dataflowSource: [avro, delimited, delta, excel, json, orc, parquet, xml, iceberg],
        dataflowSink: [avro, delimited, delta, json, orc, parquet, iceberg],
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        ...(GlobalVariables.copyJobGenericCdc ? {
            cdc: {
                supportCDC: true,
                supportedCDCDestinations: cdcDestination,
                supportGenericCdc: true
            }
        } : {}),
        supportGetMetadataActivity: true,
        mergeReadSettingsInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        getMetadataActivityReadProperties: commonFileTypeStoreGetMetadataActivityReadSettings,
        deleteActivityReadProperties: commonFileTypeStoreDeleteActivityReadSettings,
        storeReadSettingControls: getCommonFileTypeStoreReadSettingControls(true),
        storeWriteSettingControls: [
            ...commonFileTypeStoreWriteSettingControls,
            blockSizeInMbMetadata(blockSizeInMbDesc),
            {
                type: 'store-setting-metadata',
                bind: 'metadata',
                parameterizable: true
            }
        ],
        supportTestConnectionToFilePath: true,
        datasetTypeToStoreReadSettingControls: {
            [ModelConstants.binaryDataset]: getCommonBinaryStoreReadSettingControls(true)
        },
        datasetTypeToStoreWriteSettingControls: {
            [ModelConstants.binaryDataset]: [
                ...commonBinaryStoreWriteSettingControls,
                blockSizeInMbMetadata(blockSizeInMbDesc),
                {
                    type: 'store-setting-metadata',
                    bind: 'metadata',
                    options: [
                        {
                            label: '$$LASTMODIFIED',
                            value: '$$LASTMODIFIED',
                            tooltip: stringResources.Metadata_LastModified_Desc
                        }
                    ],
                    parameterizable: true
                }
            ]
        },
        locationPattern: {
            container: '([^/]*)/?[^]*',
            folderPath: '[^/]*/([^]*)/[^/]*',
            fileName: '[^]*/([^/]*)'
        },
        containerLabel: stringResources.ModelName_Container,
        containerLabelPlural: stringResources.ModelName_Containers,
        locationPropertiesRequireParameters: ['container', 'folderPath', 'fileName'],
        pathRegex: {
            pattern: Constants.blobContainerNameValidation,
            message: stringResources.CopyWizard_Dataset_Blob_Sink_ContainerValidationError
        },
        controls: [
            {
                type: 'select-switch',
                bind: 'authenticationMethod',
                label: stringResources.ModelName_AuthenticationType,
                options: mergeUserAuthCredentialOption(
                    mergeUamiCredentialOption(getDefaultAuthenticationOptions()),
                    azureStorageAuthTypes.UserAuth
                ),
                isLabelVertical: true,
                defaultValue: 'AccountKeyAuthentication',
                cases: [
                    {
                        caseValue: azureStorageAuthTypes.AccountKeyAuthentication,
                        children: [accountKeyConnectionStringSetting('blob', false, true)]
                    },
                    {
                        caseValue: azureStorageAuthTypes.SasUriAuthentication,
                        children: [sasUriSetting(false, false, true)]
                    },
                    {
                        caseValue: azureStorageAuthTypes.MSIAuthentication,
                        children: !GlobalVariables.isSamiSupportEnabled
                            ? [
                                blobEndpointSetting,
                                {
                                    type: 'management-identity-info',
                                    resourceType: stringResources.AzureBlobStorage_DisplayText,
                                    link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-blob-storage?tabs=data-factory#managed-identity'
                                }
                            ]
                            : [
                                blobEndpointSetting,
                                azureStorageAuthReferenceSetting(CredentialTypeOptions.ManagedIdentity)
                            ]
                    },
                    {
                        caseValue: azureStorageAuthTypes.ServicePrincipalAuthentication,
                        children: [
                            blobEndpointSetting,
                            azureStorageAuthReferenceSetting(CredentialTypeOptions.ServicePrincipal)
                        ]
                    },
                    {
                        // for UAMI there won't be any radio buttons, since inline mode is not supported.
                        caseValue: azureStorageAuthTypes.UserAssignedManagedIdentity,
                        children: [
                            blobEndpointSetting,
                            {
                                type: 'credential-select',
                                bind: 'credential',
                                filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }]
                            }
                        ]
                    },
                    {
                        caseValue: azureStorageAuthTypes.UserAuth,
                        children: [
                            blobEndpointSetting,
                            {
                                type: 'credential-select',
                                bind: 'credential',
                                hide: !GlobalVariables.enableConnectorUserAuthCred,
                                filterCredentialType: CredentialTypeOptions.UserAuth,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }]
                            }
                        ]
                    },
                    {
                        caseValue: azureStorageAuthTypes.Anonymous,
                        children: [blobContainerUriSetting]
                    }
                ]
            }
        ]
    };

    return metadata;
};
